/* eslint-disable @typescript-eslint/no-explicit-any */
import { Middleware, Dispatch } from "redux";
import freeze from "redux-freeze";
import createSagaMiddleware from "redux-saga";
import { showNotification } from "@shared/store/actions";
import { AccountActionTypes } from "@containers/Account/store/constants";
import { PayPerLeadActionTypes } from "@containers/PayPerLead/store/constants";
import { configureStore as configureToolkitStore } from "@reduxjs/toolkit";
import { ticketApi } from "@containers/Tickets/ticketApi";
import { setupListeners } from "@reduxjs/toolkit/query";
import { presetApi, investorFileApi } from "@containers/Contact/api";
import { webhooksApi } from "@containers/Integrations/Webhooks/api";
import { apiKeysApi } from "@containers/Integrations/ApiKeys/api";
import { stateApi } from "@shared/api/stateApi";
import { countyApi } from "@shared/api/countyApi";
import { backTestingApi } from "@containers/BackTesting/backTestingApi";
import { leadSessionApi } from "@containers/PayPerLead/store/leadSessionApi";
import { reiSiftIntegrationApi } from "@shared/api/reiSiftIntegrationApi";
import { reiSiftApi } from "@shared/api/reiSiftApi";
import { exportableFilesApi } from "@shared/api/exportablFilesApi";
import { couponApi } from "@shared/api/couponApi";
import { deployListsApi } from "@shared/api/deployListsApi";
import { companyApi } from "@shared/api/companyApi";

import appSagas from "./saga";
import rootReducer from "./reducer";

const SKIPPED_SUCCESS_MESSAGES = [
  PayPerLeadActionTypes.DISPUTE_LEAD_SUCCESS,
  AccountActionTypes.GET_ACCOUNT_ATTEMPTS_SUCCESS,
  AccountActionTypes.SAVE_ACCOUNT_ATTEMPTS_SUCCESS,
];

const errorHandlerMiddleware: Middleware = () => (next: Dispatch) => (action) => {
  if (
    action.type.includes("SUCCESS") &&
    action.payload &&
    action.payload.message &&
    !SKIPPED_SUCCESS_MESSAGES.includes(action.type)
  ) {
    next(
      showNotification({
        message: action.payload.message,
        appearance: "success",
      }),
    );
  }

  return next(action);
};

export function configureStore() {
  const isDevelopment = process.env.NODE_ENV === "development";
  const sagaMiddleware = createSagaMiddleware();
  const middleware: Array<Middleware> = [errorHandlerMiddleware, sagaMiddleware];

  if (isDevelopment) {
    middleware.push(freeze);
  }

  const store = configureToolkitStore({
    reducer: rootReducer(),
    middleware: (getDefaultMiddleware: any) =>
      getDefaultMiddleware().concat(
        ...middleware,
        ticketApi.middleware,
        deployListsApi.middleware,
        presetApi.middleware,
        presetApi.middleware,
        investorFileApi.middleware,
        webhooksApi.middleware,
        apiKeysApi.middleware,
        stateApi.middleware,
        leadSessionApi.middleware,
        countyApi.middleware,
        backTestingApi.middleware,
        reiSiftIntegrationApi.middleware,
        reiSiftApi.middleware,
        exportableFilesApi.middleware,
        couponApi.middleware,
        companyApi.middleware,
      ),
    devTools: isDevelopment,
  });

  sagaMiddleware.run(appSagas);

  // eslint-disable-next-line
  if ((module as any).hot) {
    // eslint-disable-next-line
    (module as any).hot.accept(() => store.replaceReducer(rootReducer()));
  }

  setupListeners(store.dispatch);

  return store;
}
